/**
 * ChartActionsContext — cross-tab clinical action dispatcher.
 *
 * Why this exists
 * ───────────────
 * Several clinical surfaces (the Med Referral Dialog, future
 * Treatment Plan dialogs, the Discharge wizard) need to trigger
 * actions in OTHER tabs of the chart — most commonly "open the
 * Add Medication form" or "open the Add Diagnosis form".
 *
 * Today legacy App.js owns these dialogs at its root and passes
 * raw `setShowMedicationForm` / `setSelectedClient` setters
 * through deeply nested JSX.  The V2 chart owns the same dialogs
 * but scoped to its own tab wrappers (e.g. ClientChartMedsVitalsTab
 * owns `setShowMedicationForm` locally).  Without an abstraction,
 * any cross-tab feature has to choose ONE legacy-or-V2 binding,
 * which is exactly the situation that forced the V2 Meds & Vitals
 * Phase 1 window-event bridge.
 *
 * This context normalizes those actions behind a stable API so:
 *   • Phase 2 of the Med Referral migration can consume context
 *     callbacks instead of receiving ~8 separate setter props.
 *   • Both legacy and V2 trees can wire the SAME consumer to
 *     completely different underlying state.
 *   • Future cross-tab features just consume `useChartActions()`
 *     without knowing where they're running.
 *
 * Contract
 * ────────
 *   openAddMedication(client?)
 *       Open the Add/Edit Medication dialog.  If `client` is
 *       provided, set it as the target; otherwise use whatever
 *       client the current chart already has selected.
 *
 *   openAddDiagnosis(client?)
 *       Same shape, for the Add Diagnosis dialog.
 *
 *   appendMedication(med)
 *       Optimistically push a freshly-created medication into the
 *       active chart's meds list (so the user sees it without
 *       waiting for a full re-fetch).  Implementations are free
 *       to no-op and rely on their own reload hook — the contract
 *       is "best effort UI sync".
 *
 * Safety
 * ──────
 *   Default value supplies no-op handlers that log a console.warn
 *   the first time they're called outside a provider.  That makes
 *   accidental "called too high in the tree" failures loud but
 *   non-fatal.  This file ships with NO consumers — adding
 *   consumers is Phase 2's work.
 */
import React, { createContext, useContext, useMemo } from 'react';

const _missingProviderWarn = (action) => {
  // Throttle the warning so a misbehaving render loop can't flood
  // the console.  Once per action, per page load.
  if (_missingProviderWarn._seen?.has(action)) return;
  _missingProviderWarn._seen ||= new Set();
  _missingProviderWarn._seen.add(action);
  console.warn(
    `[ChartActionsContext] ${action}() called outside a `
    + 'ChartActionsProvider — no-op.  Wrap your tree in '
    + '<ChartActionsProvider value={...}> to wire this action.',
  );
};

const defaultActions = {
  openAddMedication: () => _missingProviderWarn('openAddMedication'),
  openAddDiagnosis:  () => _missingProviderWarn('openAddDiagnosis'),
  appendMedication:  () => _missingProviderWarn('appendMedication'),
};

const ChartActionsContext = createContext(defaultActions);

export const ChartActionsProvider = ({ value, children }) => {
  // Memoize the merged value so consumers' useEffect deps stay
  // stable.  If a caller forgets to memoize their `value` prop we
  // still won't loop — useMemo runs every render but only emits a
  // new object when dependencies change.
  const merged = useMemo(
    () => ({ ...defaultActions, ...(value || {}) }),
    // The keys we care about — null values fall through to the
    // default no-ops.
    [
      value?.openAddMedication,
      value?.openAddDiagnosis,
      value?.appendMedication,
    ],
  );
  return (
    <ChartActionsContext.Provider value={merged}>
      {children}
    </ChartActionsContext.Provider>
  );
};

export const useChartActions = () => useContext(ChartActionsContext);

export default ChartActionsContext;
